import React from 'react'
import cn from 'classnames'
import { debounce } from 'lodash'
import { number, string, oneOf, oneOfType } from 'prop-types'
import { resizeImage } from '@mc/resize-image'
import { AspectRatio, ASPECT_RATIOS } from '../helpers'

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  scrollOffset?: number
  resizeOffset?: number
  resizeRound?: number
  aspectRatio?: AspectRatio
}

interface ImageState {
  calcWidth: number | undefined
  calcHeight: number | undefined
}

export default class Image extends React.PureComponent<ImageProps, ImageState> {
  static defaultProps = {
    scrollOffset: 100,
    resizeOffset: 100,
    resizeRound: 50,
    alt: '',
  }

  static propTypes = {
    src: string.isRequired,
    aspectRatio: oneOf([...ASPECT_RATIOS]),
    height: oneOfType([string, number]),
    width: oneOfType([string, number]),
    className: string,
    scrollOffset: number,
    resizeOffset: number,
    resizeRound: number,
    alt: string,
  }

  state: ImageState = { calcWidth: undefined, calcHeight: undefined }

  wrapperRef = React.createRef<HTMLDivElement>()

  componentDidMount() {
    if (!this.wrapperRef.current) {
      return
    }

    const { clientWidth, clientHeight } = this.wrapperRef.current

    this.setSize(clientWidth, clientHeight)
    window.addEventListener('resize', this.handleResize)

    const wrapperObserver = new IntersectionObserver(this.handleResize)
    wrapperObserver.observe(this.wrapperRef.current)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = debounce(() => {
    const imageRef = this.wrapperRef.current
    if (imageRef) {
      const { clientWidth, clientHeight } = imageRef
      const { resizeOffset } = this.props
      const resizedWidthDiff = Math.abs(
        clientWidth - (this.state.calcWidth ?? 0),
      )
      if (resizedWidthDiff >= (resizeOffset ?? 100)) {
        this.setSize(clientWidth, clientHeight)
      }
    }
  }, 1000)

  round = (width: number) => {
    const { resizeRound } = this.props
    const rounded = Math.ceil(width / (resizeRound ?? 50)) * (resizeRound ?? 50)
    return Math.floor(rounded)
  }

  setSize = (width: number, height: number) => {
    const ratio = width / height
    const roundedWidth = this.round(width)
    this.setState({
      calcWidth: roundedWidth,
      calcHeight: Math.round(roundedWidth / ratio),
    })
  }

  render() {
    const {
      src,
      scrollOffset,
      resizeOffset,
      resizeRound,
      aspectRatio,
      className,
      width,
      height,
      ...rest
    } = this.props
    const { calcWidth, calcHeight } = this.state
    const resizedSrc = resizeImage(src ?? null, {
      width: calcWidth,
      height: calcHeight,
      fit: 'cover',
      dpr: 2,
    })

    return (
      <div
        ref={this.wrapperRef}
        style={{ width, height }}
        className={cn(className, 'mc-img-wrapper', {
          [`mc-tile--${aspectRatio}`]: !!aspectRatio,
        })}
      >
        {!!calcWidth && (
          <img className='mc-img-wrapper__image' src={resizedSrc} {...rest} />
        )}
      </div>
    )
  }
}
